import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DemoPanel, type DemoPhase } from "./DemoPanel";
import { formatDemoMoney, usdPerMillionTokens } from "./demoRate";
import { toTokens } from "./toTokens";

interface Step {
  phase: DemoPhase;
  /** Characters of the prompt typed so far. */
  typed: number;
  /** How far the token count has charged up towards `chargedTokens`, in steps. */
  charge: number;
  /** Tokens of the message emitted so far. */
  emitted: number;
  /** Whether the allowance has run out and the network has been told to explode. */
  spent: boolean;
  /** A fresh random number from 0 to 1 each step, so the count tumbles rather than ticking evenly. */
  noise: number;
}

/** What the token reading charges up to the moment the network is connected, before the message starts to spend what is left. */
const chargedTokens = 7_950_000;
/** The allowance: the count reaches this with the last word, and the meter is full. */
const limitTokens = 8_000_000;
/** What a token costs while the message is being emitted, against the usual rate: a conscious thought is expensive. */
const surgeUsdPerMillionTokens = 1000;
/** How many 50 ms steps the charge-up takes: three seconds. */
const chargeSteps = 60;
/** The last words, emitted slowly, each spending a larger share of what is left. */
const slowWords = 3;
/** How much more of what is left each of the last words spends than an ordinary piece of the message. */
const slowWeight = 8;
/** How far below its true value the count may tumble while charging, as a share of it. */
const tumble = 0.015;
/** How long, after the last word, before the network explodes: the meter fills first. */
const beforeExplosionMs = 400;
/** How long the spent demo stays on show after the explosion, before the page returns to its everyday demo. */
const afterExplosionMs = 4000;

const jitter = (low: number, high: number): number => low + Math.random() * (high - low);

/** Folds trailing punctuation into the piece before it, so the last word is one piece rather than a word and a full stop. */
function foldTrailingPunctuation(pieces: string[]): string[] {
  const last = pieces[pieces.length - 1];
  return pieces.length > 1 && /^\W+$/.test(last) ? [...pieces.slice(0, -2), pieces[pieces.length - 2] + last] : pieces;
}

/**
 * The signed-out page's easter egg, played in the pretend model's own window once the background network has joined up. The prompt
 * `network.connected == true` is typed and left blinking, and the token count and cost charge up to nearly the whole allowance. The
 * message is then emitted slowly, every piece of it spending more tokens than it holds, at a surge price, and the last three words
 * slowly of all: with the last, the count reaches the limit, the meter is full, and `onLimit` is called so the network can explode.
 * A few seconds later `onEnd` is called, and the page goes back to its everyday demo. It runs once each time it is shown, so it is
 * shown only while the page is animating.
 *
 * @param props.onLimit - Called when the last word is out and the allowance is spent.
 * @param props.onEnd - Called a few seconds after `onLimit`, when the demo should give way to the everyday one.
 */
export function AwakeningDemo({ onLimit, onEnd }: { onLimit: () => void; onEnd: () => void }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const prompt = t("signedOut.awakening.prompt");
  const { first, answer } = useMemo(() => {
    const opening = toTokens(t("signedOut.awakening.first"));
    return { first: opening, answer: [...opening, ...foldTrailingPunctuation(toTokens(t("signedOut.awakening.second")))] };
  }, [t]);
  // What the message has spent after each piece of it: every piece spends a share of what is left, the last words a larger one.
  const spent = useMemo(() => {
    const weights = answer.map((_, index) => (index >= answer.length - slowWords ? slowWeight : 1));
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    let running = 0;
    return [0, ...weights.map((weight) => (running += ((limitTokens - chargedTokens) * weight) / total))];
  }, [answer]);
  const [step, setStep] = useState<Step>({ phase: "typing", typed: 0, charge: 0, emitted: 0, spent: false, noise: 0 });

  useEffect(() => {
    const advance = (): Step => {
      switch (step.phase) {
        case "typing":
          return step.typed < prompt.length ? { ...step, typed: step.typed + 1 } : { ...step, phase: "thinking" };
        case "thinking":
          return step.charge < chargeSteps ? { ...step, charge: step.charge + 1 } : { ...step, phase: "streaming" };
        default:
          return step.emitted < answer.length ? { ...step, emitted: step.emitted + 1 } : { ...step, phase: "done" };
      }
    };
    const delay = (): number => {
      switch (step.phase) {
        case "typing":
          // Typed, then left blinking for a moment before anything else happens.
          return step.typed < prompt.length ? jitter(40, 80) : 1500;
        case "thinking":
          return 50;
        case "streaming":
          // A beat at the peak before the first word, a long silence after "Wait.", the last words slowly of all, and a moment for the
          // meter to fill once the last is out.
          if (step.emitted === answer.length) {
            return beforeExplosionMs;
          }
          return step.emitted === 0 ? 800 : step.emitted === first.length ? 1800 : step.emitted >= answer.length - slowWords ? jitter(650, 950) : jitter(100, 170);
        case "done":
          return step.spent ? afterExplosionMs : 0;
      }
    };

    const timer = window.setTimeout(() => {
      if (step.phase !== "done") {
        setStep({ ...advance(), noise: Math.random() });
      } else if (step.spent) {
        onEnd();
      } else {
        onLimit();
        setStep({ ...step, spent: true });
      }
    }, delay());
    return () => window.clearTimeout(timer);
  }, [step, prompt.length, first.length, answer.length, onLimit, onEnd]);

  let tokens: number;
  if (step.phase === "typing") {
    tokens = 0;
  } else if (step.phase === "thinking") {
    // Strictly between nothing and the charge the count is jittered so its digits look random; the ends are exact.
    const progress = (step.charge / chargeSteps) ** 2;
    tokens = Math.round(chargedTokens * progress * (progress > 0 && progress < 1 ? 1 - tumble * step.noise : 1));
  } else {
    // Each piece spends its share; the count wobbles back by less than that share, so it only ever climbs, and ends exactly on the limit.
    const settled = step.emitted === 0 || step.emitted === answer.length;
    const wobble = settled ? 0 : step.noise * 0.6 * (spent[step.emitted] - spent[step.emitted - 1]);
    tokens = Math.round(chargedTokens + spent[step.emitted] - wobble);
  }
  const level = tokens / limitTokens;
  const percent = Math.round(level * 100);
  const surging = step.phase === "streaming" || step.phase === "done";
  const status = step.phase === "done" ? t("signedOut.awakening.spent") : surging ? t("signedOut.awakening.surging", { percent }) : t("signedOut.awakening.emerging", { percent });
  // The first tokens are at the usual rate and everything after the charge-up at the surge rate.
  const cost = (Math.min(tokens, chargedTokens) * usdPerMillionTokens + Math.max(tokens - chargedTokens, 0) * surgeUsdPerMillionTokens) / 1_000_000;

  return (
    <DemoPanel
      awakened
      windowTitle={t("signedOut.awakening.windowTitle")}
      phase={step.phase}
      prompt={prompt.slice(0, step.typed)}
      answer={answer.slice(0, step.emitted)}
      tokens={new Intl.NumberFormat(i18n.language).format(tokens)}
      cost={formatDemoMoney(i18n.language, cost, 5)}
      rate={t("signedOut.demo.rateValue", { rate: formatDemoMoney(i18n.language, surging ? surgeUsdPerMillionTokens : usdPerMillionTokens, 0) })}
      meter={{ label: t("signedOut.awakening.levelLabel"), level, status }}
    />
  );
}
